/**
 * RoleSlotEditor — Shared modal for creating/editing role slots
 *
 * Used across 5 contexts via `mode` prop:
 *   catalog  — Admin Console: full editor with translations
 *   workspace — Problem-Solving: pre-filled from AI, catalog picker
 *   template — Template Editor: definition fields + slots_needed
 *   project  — Project Work Tab / Config Modal: catalog picker + custom
 *   readonly — Volunteer Flow: display only (not used here directly)
 *
 * Architecture ref: docs/features/Life4Earth_Project_Role_Slots_Architecture.docx §3
 */

import React, { useState, useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  fetchRoleDefinitions,
  fetchCategoryDefaults,
} from "../../api/projectRoles";
import type {
  RoleDefinitionResponse,
  RoleCategoryDefaultResponse,
  ProjectRoleSlotResponse,
} from "../../api/projectRoles";
import { permissionsApiV2 } from "../../security/v2/apiClient";
import type { PermissionSetDto } from "../../security/v2/types";
import {
  ROLE_CATEGORIES,
  ROLE_PHASES,
  DIMENSION_KEYS,
  parseDimensionWeights,
  type RoleCategory,
  type EditorMode,
  type DimensionWeights,
} from "./projectTeamTypes";
import styles from "./RoleSlotEditor.module.css";

// ── Types ──

interface RoleSlotFormData {
  roleCategory: string;
  roleLabel: string;
  description: string;
  phase: string;
  slotsNeeded: number;
  visibility: string;
  isOwnerDefault: boolean;
  dimensionWeights: DimensionWeights | null; // null = use defaults
  skillsRequired: string[];
  timeCommitment: string;
  sourceDefinitionGuid: string | null;
  reportsToSlotGuid: string | null;
  /**
   * DEV-1042. The permission bundle attached to this slot. Carries the
   * operational capabilities that anyone filling this slot receives. Only
   * meaningful when the editor is in 'project' mode — other modes (catalog,
   * template, workspace) don't grant bundles directly. Required for new
   * slots in project mode; null otherwise. The actual bundle→slot grant
   * lifecycle is wired in DEV-1043's save handler.
   */
  permissionSetGuid: string | null;
}

interface Props {
  mode: EditorMode;
  /** Existing slot to edit. Null = creating new. */
  existingSlot?: ProjectRoleSlotResponse | null;
  /** All slots on this project — used to populate the "Reports To" dropdown. */
  allSlots?: ProjectRoleSlotResponse[];
  /**
   * DEV-1042. Project GUID — required when mode === 'project' to fetch
   * available bundles via permissionsApiV2.listPermissionSetsForScope.
   * Other modes don't need it.
   */
  projectGuid?: string;
  /**
   * DEV-1042. Existing bundle GUID granted to this slot, looked up by the
   * parent before opening the editor in edit mode. Pre-fills the bundle
   * picker. Null when no current grant exists or in create mode.
   */
  currentBundleGuid?: string | null;
  /** Called with form data when saving. Returns a promise so we can await completion. */
  onSave: (data: RoleSlotFormData) => void | Promise<void>;
  /** Called with form data for "Save & Add Another". */
  onSaveAndAdd?: (data: RoleSlotFormData) => void | Promise<void>;
  /** Called to delete/close this role slot. Only shown when editing an existing slot. */
  onDelete?: () => void | Promise<void>;
  /** Error message to display when delete is blocked (e.g. active assignments). */
  deleteError?: string | null;
  onClose: () => void;
}

const EMPTY_FORM: RoleSlotFormData = {
  roleCategory: "custom",
  roleLabel: "",
  description: "",
  phase: "operational",
  slotsNeeded: 1,
  visibility: "internal",
  isOwnerDefault: false,
  dimensionWeights: null,
  skillsRequired: [],
  timeCommitment: "",
  sourceDefinitionGuid: null,
  reportsToSlotGuid: null,
  permissionSetGuid: null, // DEV-1042
};

const RoleSlotEditor: React.FC<Props> = ({
  mode,
  existingSlot,
  allSlots = [],
  projectGuid,
  currentBundleGuid,
  onSave,
  onSaveAndAdd,
  onDelete,
  deleteError,
  onClose,
}) => {
  const { t } = useAppTranslation("projectTeam");

  // ── Delete confirmation state ──
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete();
      onClose();
    } catch (err) {
      // Don't close — the error will be shown via deleteError prop
      console.error('[RoleSlotEditor] Delete failed:', err);
    } finally {
      setDeleting(false);
    }
  };

  // ── Form state ──
  const [form, setForm] = useState<RoleSlotFormData>(() => {
    if (existingSlot) {
      return {
        roleCategory: existingSlot.roleCategory,
        roleLabel: existingSlot.roleLabel,
        description: existingSlot.description || "",
        phase: existingSlot.phase,
        slotsNeeded: existingSlot.slotsNeeded,
        visibility: existingSlot.visibility,
        isOwnerDefault: existingSlot.isOwnerDefault,
        dimensionWeights: existingSlot.dimensionWeights
          ? parseDimensionWeights(existingSlot.dimensionWeights)
          : null,
        skillsRequired: existingSlot.skillsRequired || [],
        timeCommitment: existingSlot.timeCommitment || "",
        sourceDefinitionGuid: existingSlot.sourceDefinitionGuid || null,
        reportsToSlotGuid: existingSlot.reportsToSlotGuid || null,
        // DEV-1042. Pre-fill from the bundle currently granted to this
        // slot, looked up by the parent before opening the editor.
        permissionSetGuid: currentBundleGuid ?? null,
      };
    }
    return { ...EMPTY_FORM };
  });

  const [saving, setSaving] = useState(false);

  // ── Catalog data ──
  const [catalogDefs, setCatalogDefs] = useState<RoleDefinitionResponse[]>([]);
  const [categoryDefaults, setCategoryDefaults] = useState<RoleCategoryDefaultResponse[]>([]);
  const [selectedCatalogCategory, setSelectedCatalogCategory] = useState("");
  const [selectedCatalogRole, setSelectedCatalogRole] = useState("");

  // ── Skill input ──
  const [skillInput, setSkillInput] = useState("");

  // ── DEV-1042. Available operational bundles for the bundle picker ──
  //
  // Filtered to grantMode === 'slot' (operational bundles only). Governance
  // bundles (Owner / Admin / Observer) are user-direct and live on the
  // Access tab's Admins section — they don't attach to slots. As of
  // migration 158 (DEV-1039 iter 3 follow-up), Contributor and Auditor
  // are slot-mode and will appear in this dropdown alongside Editor /
  // Risk Manager / External Collaborator. The endpoint returns platform +
  // project-scoped bundles for the current project; we filter client-side
  // because the filter is a UI concern and the API stays agnostic.
  const [availableBundles, setAvailableBundles] = useState<PermissionSetDto[]>([]);

  // Load catalog definitions and category defaults
  useEffect(() => {
    if (mode === "project" || mode === "workspace") {
      fetchRoleDefinitions().then(setCatalogDefs).catch(() => {});
    }
    fetchCategoryDefaults().then(setCategoryDefaults).catch(() => {});
  }, [mode]);

  // DEV-1042. Load operational bundles when in project mode.
  useEffect(() => {
    if (mode !== "project" || !projectGuid) {
      setAvailableBundles([]);
      return;
    }
    let cancelled = false;
    permissionsApiV2
      .listPermissionSetsForScope("project", projectGuid)
      .then((bundles) => {
        if (cancelled) return;
        // Filter to operational bundles. Undefined grantMode (backward-compat
        // with payloads from before DEV-1036) is treated as 'user' → excluded.
        setAvailableBundles(bundles.filter((b) => b.grantMode === "slot"));
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("[RoleSlotEditor] Failed to load bundles:", err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [mode, projectGuid]);

  // ── Derived: unique catalog categories sorted alphabetically ──
  const catalogCategories = useMemo(() => {
    const cats = [...new Set(catalogDefs.map((d) => d.roleCategory))];
    return cats.sort((a, b) => a.localeCompare(b));
  }, [catalogDefs]);

  // ── Derived: roles for the selected catalog category, sorted alphabetically ──
  const catalogRolesForCategory = useMemo(() => {
    if (!selectedCatalogCategory) return [];
    return catalogDefs
      .filter((d) => d.roleCategory === selectedCatalogCategory)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [catalogDefs, selectedCatalogCategory]);

  // ── Derived: "Reports To" options (exclude self to prevent cycles) ──
  const reportsToOptions = useMemo(() => {
    const selfGuid = existingSlot?.roleSlotGuid?.toUpperCase() ?? "";
    return allSlots
      .filter((s) => s.status !== "closed" && s.roleSlotGuid.toUpperCase() !== selfGuid)
      .sort((a, b) => a.roleLabel.localeCompare(b.roleLabel));
  }, [allSlots, existingSlot]);

  // ── Derived: effective dimension weights (form override or category default) ──
  const effectiveWeights = useMemo((): DimensionWeights => {
    if (form.dimensionWeights) return form.dimensionWeights;
    const catDefault = categoryDefaults.find(
      (d) => d.roleCategory === form.roleCategory
    );
    if (catDefault) return parseDimensionWeights(catDefault.dimensionWeights);
    return parseDimensionWeights(null);
  }, [form.dimensionWeights, form.roleCategory, categoryDefaults]);

  // ── Handlers ──

  const updateField = useCallback(
    <K extends keyof RoleSlotFormData>(key: K, value: RoleSlotFormData[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleSelectCatalogCategory = useCallback(
    (category: string) => {
      setSelectedCatalogCategory(category);
      setSelectedCatalogRole("");
      // Update form category to match
      if (category) {
        setForm((prev) => ({ ...prev, roleCategory: category }));
      }
    },
    []
  );

  const handleSelectCatalogRole = useCallback(
    (definitionGuid: string) => {
      setSelectedCatalogRole(definitionGuid);
      const def = catalogDefs.find((d) => d.definitionGuid === definitionGuid);
      if (def) {
        setForm((prev) => ({
          roleCategory: def.roleCategory,
          roleLabel: def.label,
          description: def.description || "",
          phase: "operational",
          slotsNeeded: 1,
          visibility: "internal",
          isOwnerDefault: false,
          dimensionWeights: parseDimensionWeights(def.dimensionWeights),
          skillsRequired: def.skillsSuggested || [],
          timeCommitment: def.timeCommitmentHint || "",
          sourceDefinitionGuid: def.definitionGuid,
          // DEV-963: RoleSlotFormData now requires reportsToSlotGuid. Catalog
          // selection doesn't carry a parent; user picks one in the form.
          reportsToSlotGuid: null,
          // DEV-1042. Preserve any bundle the user already picked. The
          // catalog defines role *concept*; the bundle is a separate choice
          // about what authority that role carries on THIS project.
          permissionSetGuid: prev.permissionSetGuid,
        }));
      }
    },
    [catalogDefs]
  );

  const handleDimensionChange = useCallback(
    (key: keyof DimensionWeights, value: number) => {
      setForm((prev) => ({
        ...prev,
        dimensionWeights: {
          ...(prev.dimensionWeights || effectiveWeights),
          [key]: value,
        },
      }));
    },
    [effectiveWeights]
  );

  const handleResetWeights = useCallback(() => {
    setForm((prev) => ({ ...prev, dimensionWeights: null }));
  }, []);

  const handleAddSkill = useCallback(() => {
    const skill = skillInput.trim().toLowerCase().replace(/\s+/g, "-");
    if (skill && !form.skillsRequired.includes(skill)) {
      setForm((prev) => ({
        ...prev,
        skillsRequired: [...prev.skillsRequired, skill],
      }));
    }
    setSkillInput("");
  }, [skillInput, form.skillsRequired]);

  const handleRemoveSkill = useCallback((skill: string) => {
    setForm((prev) => ({
      ...prev,
      skillsRequired: prev.skillsRequired.filter((s) => s !== skill),
    }));
  }, []);

  const handleSkillKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        handleAddSkill();
      }
    },
    [handleAddSkill]
  );

  const handleSave = useCallback(async () => {
    if (!form.roleLabel.trim()) return;
    setSaving(true);
    try {
      await onSave(form);
    } catch (err) {
      console.error("[RoleSlotEditor] Save failed:", err);
    } finally {
      setSaving(false);
    }
  }, [form, onSave]);

  const handleSaveAndAdd = useCallback(async () => {
    if (!form.roleLabel.trim() || !onSaveAndAdd) return;
    setSaving(true);
    try {
      await onSaveAndAdd(form);
      // Reset form for next entry
      setForm({ ...EMPTY_FORM });
      setSkillInput("");
    } catch (err) {
      console.error("[RoleSlotEditor] Save & Add failed:", err);
    } finally {
      setSaving(false);
    }
  }, [form, onSaveAndAdd]);

  const isNew = !existingSlot;

  return ReactDOM.createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.title}>
            {isNew ? t("editor.titleNew") : t("editor.title")}
          </span>
          <button type="button" className={styles.closeBtn} onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {/* Catalog picker — two dropdowns: Category then Role (project/workspace modes) */}
          {(mode === "project" || mode === "workspace") && isNew && catalogDefs.length > 0 && (
            <div className={styles.fieldRow}>
              <div className={`${styles.fieldGroup} ${styles.fieldHalf}`}>
                <label className={styles.fieldLabel}>
                  {t("editor.catalogCategory")}
                </label>
                <select
                  className={styles.fieldSelect}
                  value={selectedCatalogCategory}
                  onChange={(e) => handleSelectCatalogCategory(e.target.value)}
                >
                  <option value="">{t("editor.selectCategory")}</option>
                  {catalogCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {t(`categories.${cat}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className={`${styles.fieldGroup} ${styles.fieldHalf}`}>
                <label className={styles.fieldLabel}>
                  {t("editor.catalogRole")}
                </label>
                <select
                  className={styles.fieldSelect}
                  value={selectedCatalogRole}
                  onChange={(e) => handleSelectCatalogRole(e.target.value)}
                  disabled={!selectedCatalogCategory}
                >
                  <option value="">
                    {selectedCatalogCategory
                      ? t("editor.selectRole")
                      : t("editor.selectCategoryFirst")}
                  </option>
                  {catalogRolesForCategory.map((def) => (
                    <option key={def.definitionGuid} value={def.definitionGuid}>
                      {def.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Role Label */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>
              {t("editor.roleLabel")}
            </label>
            <input
              type="text"
              className={styles.fieldInput}
              value={form.roleLabel}
              onChange={(e) => updateField("roleLabel", e.target.value)}
              placeholder={t("editor.roleLabel")}
            />
          </div>

          {/* Category + Phase row */}
          <div className={styles.fieldRow}>
            <div className={`${styles.fieldGroup} ${styles.fieldHalf}`}>
              <label className={styles.fieldLabel}>
                {t("editor.category")}
              </label>
              <select
                className={styles.fieldSelect}
                value={form.roleCategory}
                onChange={(e) => updateField("roleCategory", e.target.value)}
              >
                {ROLE_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {t(`categories.${cat}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className={`${styles.fieldGroup} ${styles.fieldHalf}`}>
              <label className={styles.fieldLabel}>
                {t("editor.phase")}
              </label>
              <select
                className={styles.fieldSelect}
                value={form.phase}
                onChange={(e) => updateField("phase", e.target.value)}
              >
                {ROLE_PHASES.map((phase) => (
                  <option key={phase} value={phase}>
                    {t(`phases.${phase}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* DEV-1042. Bundle picker — operational bundles only, project mode only.
              The bundle defines what authority anyone filling this slot carries.
              Governance bundles (Owner/Admin/Observer) are user-direct and live
              on the Access tab, not here. Per migration 158, Contributor and
              Auditor are slot-mode and appear here. */}
          {mode === "project" && (
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                {t("editor.bundle")}
                <span className={styles.fieldHint}> — {t("editor.bundleHelp")}</span>
              </label>
              <select
                className={styles.fieldSelect}
                value={form.permissionSetGuid || ""}
                onChange={(e) =>
                  updateField("permissionSetGuid", e.target.value || null)
                }
              >
                <option value="">{t("editor.bundleSelect")}</option>
                {availableBundles.length === 0 ? (
                  <option value="" disabled>
                    {t("editor.bundleNoneAvailable")}
                  </option>
                ) : (
                  availableBundles.map((b) => (
                    <option
                      key={b.permissionSetGuid}
                      value={b.permissionSetGuid}
                      title={b.description}
                    >
                      {b.name}
                    </option>
                  ))
                )}
              </select>
            </div>
          )}

          {/* Reports To — hierarchy assignment (project mode only, when other slots exist) */}
          {mode === "project" && reportsToOptions.length > 0 && (
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                {t("editor.reportsTo")}
              </label>
              <select
                className={styles.fieldSelect}
                value={form.reportsToSlotGuid || ""}
                onChange={(e) =>
                  updateField(
                    "reportsToSlotGuid",
                    e.target.value || null
                  )
                }
              >
                <option value="">{t("editor.reportsToNone")}</option>
                {reportsToOptions.map((slot) => (
                  <option key={slot.roleSlotGuid} value={slot.roleSlotGuid}>
                    {slot.roleLabel}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Description */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>
              {t("editor.description")}
            </label>
            <textarea
              className={styles.fieldTextarea}
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
            />
          </div>

          {/* Slots Needed + Time Commitment row */}
          <div className={styles.fieldRow}>
            <div className={`${styles.fieldGroup} ${styles.fieldHalf}`}>
              <label className={styles.fieldLabel}>
                {t("editor.slotsNeeded")}
              </label>
              <input
                type="number"
                min={1}
                className={styles.fieldInput}
                value={form.slotsNeeded}
                onChange={(e) =>
                  updateField("slotsNeeded", Math.max(1, parseInt(e.target.value) || 1))
                }
              />
            </div>
            <div className={`${styles.fieldGroup} ${styles.fieldHalf}`}>
              <label className={styles.fieldLabel}>
                {t("editor.timeCommitment")}
              </label>
              <input
                type="text"
                className={styles.fieldInput}
                value={form.timeCommitment}
                onChange={(e) => updateField("timeCommitment", e.target.value)}
                placeholder="e.g. 4 hrs/week"
              />
            </div>
          </div>

          {/* Visibility toggle */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>
              {t("editor.visibility")}
            </label>
            <div className={styles.visibilityToggle}>
              <button
                type="button"
                className={`${styles.visToggleBtn} ${
                  form.visibility === "internal" ? styles.visToggleActive : ""
                }`}
                onClick={() => updateField("visibility", "internal")}
              >
                {t("editor.visibilityInternal")}
              </button>
              <button
                type="button"
                className={`${styles.visToggleBtn} ${
                  form.visibility === "public" ? styles.visToggleActive : ""
                }`}
                onClick={() => updateField("visibility", "public")}
              >
                {t("editor.visibilityPublic")}
              </button>
            </div>
          </div>

          {/* Skills */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>{t("editor.skills")}</label>
            <div className={styles.skillsWrapper}>
              {form.skillsRequired.map((skill) => (
                <span key={skill} className={styles.skillTag}>
                  {skill}
                  <button
                    type="button"
                    className={styles.skillRemove}
                    onClick={() => handleRemoveSkill(skill)}
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                type="text"
                className={styles.skillInput}
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyDown={handleSkillKeyDown}
                onBlur={handleAddSkill}
                placeholder={
                  form.skillsRequired.length === 0
                    ? t("editor.skillsPlaceholder")
                    : ""
                }
              />
            </div>
          </div>

          {/* Dimension Weights — editable sliders in catalog mode, read-only bars elsewhere */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>
              {t("editor.dimensionWeights")}
              {mode !== "catalog" && (
                <span className={styles.fieldHint}> — {t("editor.dimensionReadonly")}</span>
              )}
            </label>
            <div className={styles.dimensionSection}>
              {DIMENSION_KEYS.map((dim) => (
                <div key={dim} className={styles.dimensionRow}>
                  <span className={styles.dimensionLabel}>
                    {t(`dimensions.${dim}`)}
                  </span>
                  {mode === "catalog" ? (
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      className={styles.dimensionSlider}
                      value={effectiveWeights[dim]}
                      onChange={(e) =>
                        handleDimensionChange(dim, parseFloat(e.target.value))
                      }
                    />
                  ) : (
                    <div className={styles.dimensionBarBg}>
                      <div
                        className={styles.dimensionBarFill}
                        style={{ width: `${effectiveWeights[dim] * 100}%` }}
                      />
                    </div>
                  )}
                  <span className={styles.dimensionValue}>
                    {effectiveWeights[dim].toFixed(1)}
                  </span>
                </div>
              ))}
              {mode === "catalog" && (
                <div className={styles.dimensionActions}>
                  <button
                    type="button"
                    className={styles.dimActionBtn}
                    onClick={handleResetWeights}
                  >
                    {t("editor.resetDefaults")}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Owner default checkbox (project mode only) */}
          {mode === "project" && (
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={form.isOwnerDefault}
                onChange={(e) =>
                  updateField("isOwnerDefault", e.target.checked)
                }
              />
              {t("editor.ownerDefault")}
            </label>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          {/* Delete — left-aligned, only for existing slots */}
          {!isNew && onDelete && (
            <div className={styles.footerStart}>
              {deleteError ? (
                <span className={styles.deleteErrorMsg}>{deleteError}</span>
              ) : confirmingDelete ? (
                <span className={styles.deleteConfirm}>
                  <span className={styles.deleteConfirmLabel}>
                    {t("editor.deleteConfirm")}
                  </span>
                  <button
                    type="button"
                    className={styles.deleteConfirmYes}
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {t("editor.deleteYes")}
                  </button>
                  <button
                    type="button"
                    className={styles.cancelBtn}
                    onClick={() => setConfirmingDelete(false)}
                    disabled={deleting}
                  >
                    {t("editor.cancel")}
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={() => setConfirmingDelete(true)}
                >
                  {t("editor.delete")}
                </button>
              )}
            </div>
          )}

          {/* Save actions — right-aligned */}
          <div className={styles.footerEnd}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
            >
              {t("editor.cancel")}
            </button>
            {mode === "project" && isNew && onSaveAndAdd && (
              <button
                type="button"
                className={styles.saveAddBtn}
                onClick={handleSaveAndAdd}
                disabled={saving || !form.roleLabel.trim() || (mode === "project" && !form.permissionSetGuid)}
              >
                {t("editor.saveAndAdd")}
              </button>
            )}
            <button
              type="button"
              className={styles.saveBtn}
              onClick={handleSave}
              disabled={saving || !form.roleLabel.trim() || (mode === "project" && !form.permissionSetGuid)}
            >
              {t("editor.save")}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RoleSlotEditor;
export type { RoleSlotFormData };
